export function splitAssets(total: number, clientPercent: number) {
  // Use exact fractional math here so internal sums round-trip cleanly. In
  // particular, monthlyContributionAmount is stored as `yearly / 12` (already
  // fractional), so rounding the split would amplify the loss 12× when the form
  // re-derives the yearly value (e.g. $1000/yr split 50/50 was producing
  // $504/$496 instead of $500/$500). Display layers format with formatCurrency,
  // which handles dollar-rounding for presentation.
  const clientAmount = (total * clientPercent) / 100;
  const partnerAmount = total - clientAmount;
  return { clientAmount, partnerAmount };
}

export function splitFundingSourceFields(
  source: SelfFundingSource,
  clientPercent: number,
) {
  function split(value: number | null | undefined) {
    if (value == null) return null;
    return splitAssets(value, clientPercent);
  }

  const assets = split(source.existingAssetsValue);
  const contribution = split(source.monthlyContributionAmount);
  const income = split(source.monthlyIncome);
  const drawdown = split(source.drawdownAmount);

  return {
    clientOverrides: {
      ...(assets && { existingAssetsValue: assets.clientAmount }),
      ...(contribution && { monthlyContributionAmount: contribution.clientAmount }),
      ...(income && { monthlyIncome: income.clientAmount }),
      ...(drawdown && { drawdownAmount: drawdown.clientAmount }),
    },
    partnerOverrides: {
      ...(assets && { existingAssetsValue: assets.partnerAmount }),
      ...(contribution && { monthlyContributionAmount: contribution.partnerAmount }),
      ...(income && { monthlyIncome: income.partnerAmount }),
      ...(drawdown && { drawdownAmount: drawdown.partnerAmount }),
    },
  };
}

export { captureOriginalMonetaryValues, computeCombinedTotals } from '@shared';

export function findPartnerLinkedSource(
  partnerSelfFunding: Record<string, SelfFundingSource> | undefined,
  groupId: string,
): [string, SelfFundingSource] | undefined {
  if (!partnerSelfFunding) return undefined;
  return Object.entries(partnerSelfFunding).find(
    ([, ps]) => ps.fundingSourceGroupId === groupId,
  );
}

/**
 * Returns true when the given source was originated by the partner — i.e. the
 * current client is the recipient and should not be allowed to edit it. The
 * originator is identified by `addedByClientId`; older records without that
 * field can't be detected this way (callers should fall back to session
 * metadata for backward compatibility).
 */
export function isPartnerSharedRecipient(
  source: SelfFundingSource | undefined,
  currentClientId: string | undefined,
): boolean {
  if (!source || !currentClientId) return false;
  if (source.partnerAction !== 'mirrored') return false;
  if (!source.addedByClientId) return false;
  return source.addedByClientId !== currentClientId;
}
